// Combined "Location" field value helpers (#1405).
//
// A gear item's placement used to be a bare apparatus uid stored under a
// `type: 'apparatus'` field. We're generalizing that into a single "Location"
// field where apparatus is one KIND of location alongside managed non-apparatus
// locations (a storage locker, the hall, a shelf) that live in the tenant's
// `gearLocations` collection.
//
// The stored value stays a single string, namespaced so one field key holds
// either kind:
//   - `apparatus:<uid>`     — on an apparatus
//   - `gearLocation:<uid>`  — at a managed location
//   - legacy bare `<uid>`   — no colon → apparatus (existing data needs no migration)
//
// Nesting (Phase 2) rides on this for free: the value always points at the LEAF
// placement; the hierarchy lives in the `gearLocations` docs, not the value.

export const LOCATION_KINDS = {
    APPARATUS: 'apparatus',
    GEAR_LOCATION: 'gearLocation'
};

const KNOWN_KINDS = new Set([LOCATION_KINDS.APPARATUS, LOCATION_KINDS.GEAR_LOCATION]);

// A "location" field is the generalized placement field. `apparatus` is kept as
// a legacy alias so existing apparatus fields behave as location fields with no
// data migration — every render/group/count predicate goes through this.
export const isLocationField = field =>
    !!field && (field.type === 'location' || field.type === 'apparatus');

// Build the stored string for a placement. Returns '' for an empty/cleared value
// so it round-trips through react-hook-form + Firestore as "no placement".
//
// Apparatus is stored as a BARE uid (no prefix), identical to the legacy shape.
// This is deliberate: existing Firestore equality reads of the placement field —
// notably the apparatus-check↔gear join's `where(fieldId, '==', apparatusUid)`
// (checks/Edit.js) — keep matching with zero migration. Only managed locations
// carry the `gearLocation:` prefix; a bare value therefore always means apparatus
// (see parseLocationValue).
export const formatLocationValue = (kind, uid) => {
    if (!uid) return '';
    if (kind === LOCATION_KINDS.GEAR_LOCATION) return `${LOCATION_KINDS.GEAR_LOCATION}:${uid}`;
    return uid;
};

// Parse a stored value into {kind, uid}. A bare uid (no colon) or an unknown
// prefix is treated as an apparatus uid — that's the legacy shape, and defaulting
// to apparatus keeps pre-migration data resolving. Returns null for empty input.
//
// Firestore ids ([A-Za-z0-9] auto-ids, or seeded ids like `test-engine-1`) never
// contain a colon, so splitting on the FIRST colon is unambiguous.
export const parseLocationValue = value => {
    if (!value || typeof value !== 'string') return null;
    const idx = value.indexOf(':');
    if (idx === -1) {
        return {kind: LOCATION_KINDS.APPARATUS, uid: value};
    }
    const kind = value.slice(0, idx);
    const uid = value.slice(idx + 1);
    if (!uid) return null;
    if (!KNOWN_KINDS.has(kind)) {
        // Unknown prefix — treat the whole string as an apparatus uid (defensive;
        // shouldn't happen for values we write).
        return {kind: LOCATION_KINDS.APPARATUS, uid: value};
    }
    return {kind, uid};
};

// Normalize whatever a location field currently holds — a namespaced string, a
// legacy bare uid, or an already-resolved placement object — down to {kind, uid}.
export const locationParts = value => {
    if (!value) return null;
    if (typeof value === 'string') return parseLocationValue(value);
    if (typeof value === 'object' && value.uid) {
        return {kind: value.kind || LOCATION_KINDS.APPARATUS, uid: value.uid};
    }
    return null;
};

// ---------------------------------------------------------------------------
// Nested locations (Phase 2). `gearLocations` docs form a self-referential tree
// via `parentId` (present-and-null since Phase 1, so no migration). The stored
// item value still points at the LEAF placement; the hierarchy lives in the docs.
// ---------------------------------------------------------------------------

export const LOCATION_BREADCRUMB_SEP = ' › ';

// Root→leaf chain of {uid, name} for a location uid. `byId` is a Map(uid → doc).
// Guards against a broken/cyclic parentId chain by tracking visited ids.
export const locationPath = (uid, byId) => {
    const chain = [];
    if (!uid || !byId) return chain;
    const seen = new Set();
    let cursor = byId.get(uid);
    while (cursor && cursor.uid && !seen.has(cursor.uid)) {
        seen.add(cursor.uid);
        chain.unshift({uid: cursor.uid, name: cursor.name || '(unnamed)'});
        cursor = cursor.parentId ? byId.get(cursor.parentId) : null;
    }
    return chain;
};

// "Storage › Section A › Shelf 3" for a location uid; '' when unresolved.
export const locationBreadcrumb = (uid, byId, sep = LOCATION_BREADCRUMB_SEP) =>
    locationPath(uid, byId).map(n => n.name).join(sep);

// Index the flat `gearLocations` list into a tree: byId (uid → doc),
// childrenById (parentId|null → sorted docs), and roots (top-level docs).
export const buildLocationIndex = locations => {
    const byId = new Map();
    const childrenById = new Map();
    (locations || []).forEach(l => {
        if (l && l.uid) byId.set(l.uid, l);
    });
    byId.forEach(l => {
        // A parentId pointing at a missing/archived doc is treated as root so the
        // node stays reachable rather than orphaned.
        const parentId = l.parentId && byId.has(l.parentId) ? l.parentId : null;
        if (!childrenById.has(parentId)) childrenById.set(parentId, []);
        childrenById.get(parentId).push(l);
    });
    childrenById.forEach(list => list.sort((a, b) => (a.name || '').localeCompare(b.name || '')));
    return {byId, childrenById, roots: childrenById.get(null) || []};
};

// Every descendant uid of `uid` (excluding the node itself).
export const descendantIds = (uid, childrenById) => {
    const out = new Set();
    if (!childrenById) return out;
    const stack = [...(childrenById.get(uid) || [])];
    while (stack.length) {
        const n = stack.pop();
        if (!n || out.has(n.uid)) continue;
        out.add(n.uid);
        stack.push(...(childrenById.get(n.uid) || []));
    }
    return out;
};

// A re-parent creates a cycle when the proposed parent is the node itself or one
// of its descendants — walk up from newParentId and see if we reach uid.
export const wouldCreateCycle = (uid, newParentId, byId) => {
    if (!newParentId) return false;
    if (newParentId === uid) return true;
    if (!byId) return false;
    const seen = new Set();
    let cursor = byId.get(newParentId);
    while (cursor && cursor.uid && !seen.has(cursor.uid)) {
        if (cursor.uid === uid) return true;
        seen.add(cursor.uid);
        cursor = cursor.parentId ? byId.get(cursor.parentId) : null;
    }
    return false;
};

// Resolve a stored value to a normalized placement object for display. The
// underlying doc (apparatus or gearLocation) is spread in so existing readers of
// `.tag` / `.uid` / `.image` keep working, and `.kind` / `.label` give new code a
// kind-agnostic display string. For a managed location, `.path` (root→leaf) and
// `.breadcrumb` are derived from the parentId chain — `.label` stays the LEAF
// name (grid cell), `.breadcrumb` the full path (tooltip). Returns null when
// there's no value.
export const resolveLocation = (value, {apparatusById, gearLocationsById} = {}) => {
    const parsed = locationParts(value);
    if (!parsed) return null;
    const {kind, uid} = parsed;
    if (kind === LOCATION_KINDS.GEAR_LOCATION) {
        const doc = gearLocationsById && gearLocationsById.get(uid);
        const path = locationPath(uid, gearLocationsById);
        const breadcrumb = path.map(n => n.name).join(LOCATION_BREADCRUMB_SEP);
        return {...(doc || {}), _location: true, kind, uid, label: doc ? doc.name : null, path, breadcrumb: breadcrumb || null};
    }
    const doc = apparatusById && apparatusById.get(uid);
    return {...(doc || {}), _location: true, kind: LOCATION_KINDS.APPARATUS, uid, label: doc ? doc.tag : null, path: [], breadcrumb: null};
};

// A stable grouping key for a placement value, kind-namespaced so an apparatus and
// a gearLocation that happen to share a uid never collapse together. '' → empty.
export const locationGroupKey = value => {
    const parts = locationParts(value);
    return parts ? `${parts.kind}:${parts.uid}` : '';
};
